import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { EconomicsPanel } from "../../components/admin/EconomicsPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const COLS = [["affirmation_writing", "Affirmation writing"], ["own_voice", "Own voice"], ["guide_chat", "Guide chat"],
  ["coach_chat", "Coach chat"], ["spoken_replies", "Spoken replies"], ["speech_to_text", "Speech to text"]];
const gbp = (v, d = 2) => `${v < 0 ? "-" : ""}£${Math.abs(v || 0).toFixed(d)}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

const Totals = ({ t }) => (
  <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-costs-totals">
    {[["AI + voice cost", gbp(t.cost)], ["Paid this month", gbp(t.paid)], ["Own voice", gbp(t.own_voice)], ["Coach + guide chat", gbp((t.coach_chat || 0) + (t.guide_chat || 0))]].map(([l, v]) => (
      <div key={l} className="rounded-xl border border-[#e5e0d3] bg-white p-4">
        <p className="text-xs uppercase tracking-wider text-[#6b6b6b]">{l}</p>
        <p className="mt-1 text-2xl text-[#2b2b2b]">{v}</p>
      </div>
    ))}
  </div>
);

const Row = ({ r }) => (
  <tr className="border-t border-[#eee9dc]" data-testid={`admin-costs-row-${r.user_id}`}>
    <td className="py-2 pr-3"><div className="text-[#2b2b2b]">{r.email}</div><div className="text-xs text-[#8a8a8a]">{r.plan}{r.last_purchase ? ` · last: ${r.last_purchase}` : ""}</div></td>
    {COLS.map(([k]) => <td key={k} className="py-2 px-2 text-right tabular-nums text-[#4b4b4b]">{r.costs[k] ? gbp(r.costs[k], 3) : "–"}</td>)}
    <td className="py-2 px-2 text-right tabular-nums font-medium text-[#2b2b2b]">{gbp(r.total_cost, 3)}</td>
    <td className="py-2 px-2 text-right tabular-nums">{gbp(r.paid_month)}<div className="text-xs text-[#8a8a8a]">{gbp(r.paid_lifetime)} total</div></td>
    <td className={`py-2 pl-2 text-right tabular-nums ${r.margin_month < 0 ? "text-[#a0522d]" : "text-[#4A6741]"}`}>{gbp(r.margin_month)}</td>
  </tr>
);

export default function AdminCosts() {
  const [ym, setYm] = useState(thisMonth());
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    axios.get(`${API}/api/admin/costs/users`, { params: { ym }, headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } })
      .then((r) => setD(r.data)).catch((e) => setErr(e.response?.status === 403 ? "Admins only." : "Couldn't load costs."));
  }, [ym]);
  return (
    <div className="min-h-screen bg-[#f6f4ee] px-5 py-10" data-testid="admin-costs-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl text-[#2b2b2b]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Cost per user</h1>
            <p className="mt-1 text-sm text-[#6b6b6b]">Logged AI and voice cost each month, next to what each user pays.</p>
          </div>
          <input type="month" value={ym} onChange={(e) => setYm(e.target.value || thisMonth())} data-testid="admin-costs-month"
            className="rounded-lg border border-[#d9d4c7] bg-white px-3 py-2 text-sm" />
        </div>
        <EconomicsPanel ym={ym} />
        {err && <p className="mt-6 text-[#a0522d]" data-testid="admin-costs-error">{err}</p>}
        {!d && !err && <Loader2 className="mt-10 animate-spin text-[#4A6741]" />}
        {d && (
          <>
            <Totals t={d.totals} />
            <div className="mt-6 overflow-x-auto rounded-2xl border border-[#e5e0d3] bg-white p-4">
              {!d.rows.length ? <p className="text-sm text-[#6b6b6b]" data-testid="admin-costs-empty">Nothing logged for this month yet.</p> : (
                <table className="w-full text-sm" data-testid="admin-costs-table">
                  <thead><tr className="text-xs uppercase tracking-wider text-[#8a8a8a]">
                    <th className="text-left pb-2">User</th>{COLS.map(([k, l]) => <th key={k} className="text-right pb-2 px-2">{l}</th>)}
                    <th className="text-right pb-2 px-2">Total cost</th><th className="text-right pb-2 px-2">Paid</th><th className="text-right pb-2 pl-2">Left over</th>
                  </tr></thead>
                  <tbody>{d.rows.map((r) => <Row key={r.user_id} r={r} />)}</tbody>
                </table>
              )}
            </div>
            <p className="mt-3 text-xs text-[#8a8a8a]">{d.note} Chat, spoken-reply and speech-to-text costs are logged from this update onwards.</p>
          </>
        )}
      </div>
    </div>
  );
}
